import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

/** True if the browser has either our auth cookie or our local-storage token. */
function hasAuthArtifact() {
  if (typeof window === "undefined") return false;
  if (localStorage.getItem("mara_token")) return true;
  // Cookie is httpOnly so we can't read it directly — but we set a non-httpOnly
  // `mara_session=1` flag at login to signal "we have a session" without leaking the token.
  return document.cookie.split(";").some((c) => c.trim().startsWith("mara_session="));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = anon, object = logged in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!hasAuthArtifact()) {
        setUser(false);
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("mara_token", data.access_token);
      // Set a lightweight non-httpOnly flag so AuthContext knows a session exists on next mount.
      document.cookie = "mara_session=1; path=/; samesite=lax";
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    localStorage.removeItem("mara_token");
    document.cookie = "mara_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
